import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { api } from '@/api/client';
import { refreshCallLists } from './refreshCallLists';

/**
 * The outcome of one call, picked from the Settings → IVR Call Remarks master.
 *
 * Saved the moment it is chosen, with no Save button: an agent on the phone all day will not press
 * a second button, and a note that is only sometimes saved is worse than no note. The row rolls back
 * to its previous value if the save is refused, so the screen never shows a remark that was not
 * stored.
 *
 * Deliberately separate from the free-text remark asked for before dialling: that one says what the
 * agent MEANT to call about, this one says what came of it, and collapsing them into one field would
 * lose whichever was written first.
 */
export function CustomerRemarkSelect({ callId, value, onSaved }: {
  callId: string;
  value?: string | null;
  onSaved?: (v: string | null) => void;
}) {
  const qc = useQueryClient();
  // What the row SHOWS. Held here so the pick sticks the moment it is made: the list above is cached
  // for a minute, and a select driven only by that cache snapped back to the old remark until the
  // page was reloaded (operator, 24-10: "should be fixed after selection without refresh").
  const [shown, setShown] = useState<string>(value ?? '');
  useEffect(() => { setShown(value ?? ''); }, [value]);

  // Cached for the session — the master changes once in a blue moon, and refetching it per row
  // would fire one request per call on screen.
  const { data: options } = useQuery({
    queryKey: ['settings', 'ivr_remarks'],
    queryFn: async () => {
      try {
        const d = (await api.get('/settings/ivr_remarks')).data as any;
        return (d?.items ?? []).map((i: any) => String(i?.name ?? '').trim()).filter(Boolean) as string[];
      } catch { return [] as string[]; }
    },
    staleTime: 5 * 60_000,
  });

  const save = useMutation({
    mutationFn: async (v: string) =>
      (await api.patch(`/ivr/calls/${callId}/remarks`, { customerRemarks: v })).data,
    onSuccess: (_d, v) => {
      onSaved?.(v || null);
      toast.success(v ? `Saved: ${v}` : 'Remark cleared');
      // Every call list on screen — including the NDR queue, whose "IVR / Call" column the server has
      // just updated — refetched, so nothing needs a reload.
      refreshCallLists(qc);
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? 'Could not save the remark');
      setShown(value ?? '');      // put the row back to what is actually stored
      onSaved?.(value ?? null);
    },
  });

  // An empty master is an administrator's omission, not the agent's — say where to fix it rather
  // than showing a dropdown with nothing in it.
  if (!options?.length) {
    return (
      <span className="text-[11px] text-slate-400" title="Settings → Master Data → IVR Call Remarks">
        No remarks set up
      </span>
    );
  }

  const current = shown;
  // A value saved before the master was edited must stay visible, or the row would silently appear
  // blank and the next save would wipe a remark nobody meant to change.
  const list = current && !options.includes(current) ? [current, ...options] : options;

  return (
    <div className="flex items-center gap-1">
      <select
        value={current}
        disabled={save.isPending}
        onChange={(e) => { setShown(e.target.value); onSaved?.(e.target.value || null); save.mutate(e.target.value); }}
        className={`text-xs px-1.5 py-1 border rounded max-w-[13rem] truncate outline-none focus:border-primary disabled:opacity-60 ${
          current ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-slate-200 text-slate-500'
        }`}
      >
        <option value="">— not recorded —</option>
        {list.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      {save.isPending && <Loader2 size={11} className="animate-spin text-slate-400" />}
    </div>
  );
}
